import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddWordModal = ({ isOpen, onClose, onAdd, plays }) => {
  const [word, setWord] = useState('');
  const [pronunciation, setPronunciation] = useState('');
  const [definition, setDefinition] = useState('');
  const [selectedPlays, setSelectedPlays] = useState([]);
  const [errors, setErrors] = useState({});

  const playOptions = plays?.map(play => ({
    value: play?.id,
    label: play?.title
  }));

  const validateForm = () => {
    const newErrors = {};
    if (!word?.trim()) newErrors.word = 'Word is required';
    if (!definition?.trim()) newErrors.definition = 'Definition is required';
    if (selectedPlays?.length === 0) newErrors.plays = 'Select at least one play';
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onAdd({
        word: word?.trim(),
        pronunciation: pronunciation?.trim(),
        definition: definition?.trim(),
        plays: selectedPlays
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setWord('');
    setPronunciation('');
    setDefinition('');
    setSelectedPlays([]);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-foreground/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Plus" size={20} className="text-primary" />
            </div>
            <h2 className="text-xl font-bold text-foreground">Add New Word</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <Input
            label="Archaic Word"
            type="text"
            placeholder="e.g., thou, thee, hath"
            value={word}
            onChange={(e) => setWord(e?.target?.value)}
            error={errors?.word}
            required
          />

          <Input
            label="Pronunciation Guide"
            type="text"
            placeholder="e.g., /ˈθaʊ/"
            description="Optional phonetic pronunciation"
            value={pronunciation}
            onChange={(e) => setPronunciation(e?.target?.value)}
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Modern Definition <span className="text-error">*</span>
            </label>
            <textarea
              value={definition}
              onChange={(e) => setDefinition(e?.target?.value)}
              placeholder="Enter the modern English definition..."
              className="w-full min-h-32 px-4 py-3 bg-background border border-border rounded-md text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {errors?.definition && (
              <p className="text-sm text-error mt-1">{errors?.definition}</p>
            )}
          </div>

          <Select
            label="Associated Plays"
            description="Select plays where this word appears"
            options={playOptions}
            value={selectedPlays}
            onChange={setSelectedPlays}
            multiple
            searchable
            error={errors?.plays}
            required
          />

          <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">
                  Audio Upload
                </p>
                <p className="text-sm text-muted-foreground">
                  You can upload pronunciation audio after creating the word entry
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleSubmit}>
            Add Word
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddWordModal;